import { Button, DatePicker, Flex, Form, Select, Space, Table } from 'antd'
import Title from 'antd/es/typography/Title'
import Text from 'antd/es/typography/Text'
import { FC, Key, useMemo, useState } from 'react'
import { Dayjs } from 'dayjs'
import { PlusOutlined, MinusOutlined } from '@ant-design/icons'
import TableWrapper from '../../../../components/table-wrapper/table-wrapper'
import renderDatetime from '../../../../helpers/tables/render-datetime'
import { useGetMachinesQuery } from '../../../../store/api/machines-api'
import { useGetPrintJobsQuery } from '../../../../store/api/print-jobs-api'
import { JobStatuses } from '../../../../types/api/print-job'
import { DocumentId, EndpointQueryParameters } from '../../../../types/strapi/strapi'
import { materialRequirementsPopulate } from '../../../schedules/flexo/constants/shipment-populate'
import { CreatedPrintJobsTab } from '../../../schedules/flexo/components/created-print-jobs/types'
import { columns } from '../../columns'
import { outputDateFormat } from '../../../../constants/constants'

const { RangePicker } = DatePicker

const flexoMachinePrintTypeFilter = {
    name: { $in: ['flexography', 'dummy'] },
}

const digitalMachineBrandFilter = {
    name: { $in: ['Durst', 'GF'] },
}

const basePopulate: EndpointQueryParameters['populate'] = {
    shipment: {
        populate: {
            stamp: { populate: ['tube', 'roller'] },
            machine: true,
            additional_machines: true,
            client: true,
            shipment_positions: true,
            materialRequirements: { populate: materialRequirementsPopulate },
            calculated: true,
            calculatedOnActual: true,
        },
    },
    print_job_executors: {
        populate: {
            user: true,
            machine: true,
        },
    },
    print_queue_entries: {
        populate: {
            machine: true,
        },
    },
}

interface PrintHistoryFilters {
    machineDocumentId?: DocumentId
    dateRange?: [Dayjs, Dayjs] | null
}

interface PrintHistoryTableProps {
    tab: CreatedPrintJobsTab
}

const PrintHistoryTable: FC<PrintHistoryTableProps> = ({ tab }) => {
    const [filtersForm] = Form.useForm<PrintHistoryFilters>()
    const machineFilter = Form.useWatch('machineDocumentId', filtersForm)
    const dateRange = Form.useWatch('dateRange', filtersForm)
    const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([])

    const machinesQueryParams = useMemo((): EndpointQueryParameters => {
        if (tab === 'flexo') {
            return {
                filters: { machine_print_type: flexoMachinePrintTypeFilter },
                pagination: { pageSize: 500, page: 1 },
            }
        }

        return {
            filters: { machine_brand: digitalMachineBrandFilter },
            pagination: { pageSize: 500, page: 1 },
        }
    }, [tab])

    const { data: machinesData, isSuccess: isMachinesLoaded } = useGetMachinesQuery(machinesQueryParams)
    const machines = useMemo(() => machinesData?.data ?? [], [machinesData])
    const machineDocIds = useMemo(
        () => machines.map(machine => machine.documentId).filter(Boolean) as DocumentId[],
        [machines]
    )

    const params = useMemo((): EndpointQueryParameters => {
        const selectedMachineIds =
            machineFilter && machineDocIds.includes(machineFilter)
                ? [machineFilter]
                : machineDocIds

        const shipmentFilters: Record<string, unknown> = {
            machine: {
                documentId: { $in: selectedMachineIds },
            },
        }

        if (dateRange?.[0] && dateRange?.[1]) {
            shipmentFilters.clientDeliveryDate = {
                $gte: dateRange[0].startOf('day').toISOString(),
                $lte: dateRange[1].endOf('day').toISOString(),
            }
        }

        return {
            populate: basePopulate,
            sort: ['id:desc'],
            filters: {
                jobStatus: { $eq: JobStatuses.Completed },
                shipment: shipmentFilters,
            },
        }
    }, [machineDocIds, machineFilter, dateRange])

    const { data } = useGetPrintJobsQuery(params, {
        skip: !isMachinesLoaded || machineDocIds.length === 0,
    })
    const dataSource = useMemo(
        () => (machineDocIds.length === 0 ? [] : data?.data || []),
        [machineDocIds, data]
    )

    const allRowKeys = useMemo(() => dataSource.map(job => job.id), [dataSource])
    const allExpanded = allRowKeys.length > 0 && expandedRowKeys.length === allRowKeys.length

    return (
        <TableWrapper style={{ flex: 'auto' }}>
            <Table
                title={() => (
                    <Flex justify='space-between' align='center' wrap gap={12}>
                        <Title style={{ marginTop: 0, marginBottom: 0 }} level={4}>
                            История
                        </Title>
                        <Space wrap>
                            <Form form={filtersForm} component={false}>
                                <Space wrap>
                                    <Form.Item name='machineDocumentId' noStyle>
                                        <Select
                                            allowClear
                                            placeholder='Станок'
                                            style={{ minWidth: 160 }}
                                            options={machines.map(machine => ({
                                                value: machine.documentId,
                                                label: machine.name,
                                            }))}
                                            showSearch
                                            filterOption={(input, option) =>
                                                (option?.label?.toString().toLowerCase() ?? '').includes(
                                                    input.toLowerCase()
                                                )
                                            }
                                        />
                                    </Form.Item>
                                    <Form.Item name='dateRange' noStyle>
                                        <RangePicker format={outputDateFormat} />
                                    </Form.Item>
                                </Space>
                            </Form>
                            <Button
                                icon={allExpanded ? <MinusOutlined /> : <PlusOutlined />}
                                onClick={() =>
                                    setExpandedRowKeys(allExpanded ? [] : allRowKeys)
                                }
                                disabled={allRowKeys.length === 0}
                            >
                                {allExpanded ? 'Свернуть все' : 'Развернуть все'}
                            </Button>
                        </Space>
                    </Flex>
                )}
                dataSource={dataSource}
                columns={columns}
                pagination={false}
                rowKey={'id'}
                size={'small'}
                expandable={{
                    expandedRowKeys,
                    onExpandedRowsChange: keys => setExpandedRowKeys([...keys]),
                    expandedRowRender(printJob) {
                        return (
                            <Flex gap={20}>
                                <div>
                                    <Text strong>Фиксация работ</Text>
                                    {(printJob.print_job_executors || []).length === 0 ? (
                                        <div>Нет работ</div>
                                    ) : (
                                        <Table
                                            columns={[
                                                {
                                                    dataIndex: ['user', 'username'],
                                                    title: 'Печатник',
                                                    width: 120,
                                                },
                                                {
                                                    dataIndex: ['machine', 'name'],
                                                    title: 'Станок',
                                                    width: 80,
                                                },
                                                {
                                                    dataIndex: ['start'],
                                                    title: 'Начало',
                                                    width: 120,
                                                    render: renderDatetime,
                                                },
                                                {
                                                    dataIndex: ['end'],
                                                    title: 'Окончание',
                                                    width: 120,
                                                    render: v => v && renderDatetime(v),
                                                },
                                                {
                                                    dataIndex: ['reason'],
                                                    width: 120,
                                                    title: 'Причина',
                                                },
                                            ]}
                                            dataSource={printJob.print_job_executors}
                                            rowKey={'documentId'}
                                            pagination={false}
                                        />
                                    )}
                                </div>
                                <div>
                                    <Text strong>Очереди</Text>
                                    {(printJob.print_queue_entries || []).length === 0 ? (
                                        <div>Нет очередей</div>
                                    ) : (
                                        <Table
                                            columns={[
                                                {
                                                    dataIndex: ['machine', 'name'],
                                                    title: 'Станок',
                                                    width: 100,
                                                },
                                                {
                                                    dataIndex: ['zone'],
                                                    title: 'Зона',
                                                    width: 100,
                                                },
                                            ]}
                                            dataSource={printJob.print_queue_entries}
                                            rowKey={'documentId'}
                                            pagination={false}
                                        />
                                    )}
                                </div>
                            </Flex>
                        )
                    },
                }}
            />
        </TableWrapper>
    )
}

export default PrintHistoryTable
